/**
 * The bench's row drawer, opened against one analysis from the Assignments
 * tables.
 *
 * No second verdict UI: this fetches the row and hands it to `BenchRowView`,
 * the same component the bench uses, writing through the same endpoint. A
 * parallel form here would be a second rubric nobody calibrated.
 *
 * The matrix is fetched with `rowId` — an assignment dataset holds a row per
 * submission, and this drawer wants exactly one of them.
 * See docs/FeatureDesigns/Evals/assignment-step-verdicts.md §2.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../../../api/adminApi';
import { BenchRowView } from '../../scenario-evals/BenchRowView';
import '../../scenario-evals/BenchTab.css';

export function StepVerdictDrawer({ datasetId, scenarioInputId, onClose }: {
    datasetId: string;
    scenarioInputId: string;
    onClose: () => void;
}) {
    const queryClient = useQueryClient();
    const { data, isLoading, error } = useQuery({
        queryKey: ['benchRow', datasetId, scenarioInputId],
        queryFn: () => adminApi.getBenchMatrix(datasetId, true, scenarioInputId),
    });

    const row = data?.rows?.[0];

    // Everything before the row arrives still belongs in the drawer's place on
    // screen. Rendering it inline puts "Loading steps…" at the foot of a
    // 25-row table, nowhere near the click that caused it.
    if (isLoading || error || !row) {
        return (
            <Shell onClose={onClose}>
                {isLoading && <p className="bench-muted">Loading steps…</p>}
                {error && <p className="bench-error">Could not load steps: {(error as Error).message}</p>}
                {/* Capture reported a row and the matrix has none: the reads
                    raced, or the row left the dataset between them. */}
                {!isLoading && !error && !row
                    && <p className="bench-error">That analysis is no longer in the eval dataset.</p>}
            </Shell>
        );
    }

    return (
        <BenchRowView
            datasetId={datasetId}
            row={row}
            onClose={onClose}
            onChanged={() => {
                queryClient.invalidateQueries({ queryKey: ['benchRow', datasetId, scenarioInputId] });
                // The table's chips come from the analyses read, not from here.
                queryClient.invalidateQueries({ queryKey: ['assignmentAnalyses'] });
            }}
        />
    );
}

/** The drawer's frame, for the states that have no row to hand BenchRowView. */
function Shell({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
    return (
        <div className="bench-modal-backdrop" onClick={onClose}>
            <div className="bench-drawer" onClick={e => e.stopPropagation()}>
                <div className="bench-drawer-head">
                    <h2>Judge steps</h2>
                    <button className="btn btn-secondary" onClick={onClose}>Close</button>
                </div>
                <div className="bench-drawer-body">{children}</div>
            </div>
        </div>
    );
}
